import { Icon } from '@honeycomb-npm/honeycomb-react';

import { useEffect, useState } from 'react';

import styled from 'styled-components';

import Origination from './components/OriginationTemplate';
import OriginationBlade from './components/OriginationBlade';
import useBladeFlow from './useBladeFlow';
import Blades from './app/navigation/blades/Blades';
import { type ApplicationDataContent, BLADE_NAME, type BladeNameT } from './app/navigation/types';
import { selectApplicationContent, selectDefaultBlade, selectExistingUrl } from './app/netBank/netBankSelectors';
import { useAppSelector } from './stories/hooks';
import redirectTo from './utils/redirectTo';
import Disclaimer from './components/Disclaimer';
import DiscoverMore from './components/DiscoverMore';
import Spinner from './components/Spinner/FullScreenSpinner';
import { selectSpinnerStatus } from './components/Spinner/spinnerSelectors';
import './styles/tooltip.scss';

export const BladeDiv = styled.div`
&&& {
    display: flex;
    justify-content: space-around;
    flex-direction: column;
}`;

export const ComponentDiv = styled.div`
&&& {
   flex: auto;
   display:flex;
   flex-direction: column;
   justify-content: center;
   
   &&& > button {
    align-self:flex-start;
   }
}`;

type ThingsYouShouldKnow = {
    header: string,
    content: string,
}

type TermsAndConditions = {
    header: string,
    content: string,
}

export const DefaultThingsYouShouldKnow: ThingsYouShouldKnow = {
    header: 'Things you should know',
    content: 'This is a disclaimer.',
};

export const DefaultTermsAndConditions: TermsAndConditions = {
    header: 'Show all terms & conditions',
    content: 'Terms and conditions content',
};

const App = () => {
    const defaultBladeName = useAppSelector(selectDefaultBlade);
    const existingURL = useAppSelector(selectExistingUrl);

    const [thingsYouShouldKnow, setThingsYouShouldKnow] = useState(DefaultThingsYouShouldKnow);
    const [termsAndConditions, setTermsAndConditions] = useState(DefaultTermsAndConditions);

    const {
        blades, attachRef, navigateToBlade, focusedBlade, bladeHeaderMain, bladeHeaderContent,
    } = useBladeFlow<BladeNameT>(defaultBladeName);

    const onClickClose = () => {
        redirectTo(existingURL);
    };

    const applicationContent: string = useAppSelector(selectApplicationContent);
    const isSpinnerVisible: boolean = useAppSelector(selectSpinnerStatus);

    useEffect(() => {
        if (applicationContent) {
            const parsedContent: ApplicationDataContent = JSON.parse(applicationContent) as ApplicationDataContent;

            if (parsedContent.thingsYouShouldKnow) {
                setThingsYouShouldKnow(parsedContent.thingsYouShouldKnow);
            }

            if (parsedContent.termsAndConditions) {
                setTermsAndConditions(parsedContent.termsAndConditions);
            }
        }
    }, []);

    return (
        <>
            { isSpinnerVisible && <Spinner /> }
            <Origination
                isCloseEnabled={false}
                isHidden={false}
                onClickCloseButton={onClickClose}
                id="BorrowingEstimatorClose"
                headerMain={bladeHeaderMain}
                headerContent={bladeHeaderContent}
            >
                {blades.map((bladeName, index, arr) => {
                    const {
                        isBlockDisplay, Component, prev, forcePrev,
                    } = Blades[bladeName];

                    const prevBlade: BladeNameT = forcePrev ? prev as BladeNameT : arr[index - 1] ?? prev as BladeNameT;

                    return (
                        <OriginationBlade
                            isBlockDisplay={isBlockDisplay}
                            fullWidth
                            key={bladeName}
                            ref={attachRef(bladeName)}
                        >
                            {prev && (
                                <Icon
                                    id={`${bladeName}-blade-back-button`}
                                    data-testid={`${bladeName}-blade-back-button`}
                                    symbol="controlPageUp24"
                                    onClick={() => navigateToBlade(prevBlade, true)}
                                >
                                    Previous step
                                </Icon>
                            )}
                            <ComponentDiv>
                                <Component isFocusedBlade={focusedBlade === bladeName} />
                            </ComponentDiv>
                            {
                                (bladeName === BLADE_NAME.BORROWING_POWER) && index === arr.length - 1 && (
                                    <DiscoverMore />
                                )
                            }
                            {
                                index === arr.length - 1 && (
                                    <Disclaimer
                                        heading={thingsYouShouldKnow.header}
                                        description={thingsYouShouldKnow.content}
                                        accordionHeader={termsAndConditions.header}
                                        accordionContent={termsAndConditions.content}
                                    />
                                )
                            }
                        </OriginationBlade>
                    );
                })}
            </Origination>
        </>
    );
};

export default App;
